import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import SlaAPI from '../../api/sla';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { SLA_EVENTS } from '../../helper/AnalyticsHelper/events';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
    isSoftDeleting: false,
    isUpdating: false,
    isReactivating: false,
  },
};

export const getters = {
  getSLA(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async function get({ commit }) {
    commit(types.SET_SLA_UI_FLAG, { isFetching: true });
    try {
      const response = await SlaAPI.get();
      commit(types.SET_SLA, response.data.payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_SLA_UI_FLAG, { isFetching: false });
    }
  },

  create: async function create({ commit }, slaObj) {
    commit(types.SET_SLA_UI_FLAG, { isCreating: true });
    try {
      const response = await SlaAPI.create(slaObj);
      AnalyticsHelper.track(SLA_EVENTS.CREATE);
      commit(types.ADD_SLA, response.data.payload);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_SLA_UI_FLAG, { isCreating: false });
    }
  },

  delete: async function deleteSla({ commit }, id) {
    commit(types.SET_SLA_UI_FLAG, { isDeleting: true });
    try {
      await SlaAPI.delete(id);
      AnalyticsHelper.track(SLA_EVENTS.DELETED);
      commit(types.DELETE_SLA, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_SLA_UI_FLAG, { isDeleting: false });
    }
  },

  softDelete: async function softDelete({ commit }, id) {
    commit(types.SET_SLA_UI_FLAG, { isSoftDeleting: true });
    try {
      await SlaAPI.softDelete(id);
      AnalyticsHelper.track(SLA_EVENTS.SOFT_DELETED);
      commit(types.SOFT_DELETE_SLA, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_SLA_UI_FLAG, { isSoftDeleting: false });
    }
  },

  update: async function update({ commit, dispatch }, { id, slaObj }) {
    commit(types.SET_SLA_UI_FLAG, { isUpdating: true });
    try {
      await SlaAPI.update(id, slaObj);
      // Reload the SLA list to get updated data
      await dispatch('get');
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_SLA_UI_FLAG, { isUpdating: false });
    }
  },

  reactivate: async function reactivate({ commit, dispatch }, id) {
    commit(types.SET_SLA_UI_FLAG, { isReactivating: true });
    try {
      await SlaAPI.reactivate(id);
      // Reload the SLA list to get updated data
      await dispatch('get');
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_SLA_UI_FLAG, { isReactivating: false });
    }
  },

  hardDelete: async function hardDelete({ commit, dispatch }, id) {
    commit(types.SET_SLA_UI_FLAG, { isDeleting: true });
    try {
      await SlaAPI.hardDelete(id);
      AnalyticsHelper.track(SLA_EVENTS.DELETED);
      // Reload the SLA list to get updated data
      await dispatch('get');
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_SLA_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_SLA_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_SLA]: MutationHelpers.set,
  [types.ADD_SLA]: MutationHelpers.create,
  [types.EDIT_SLA]: MutationHelpers.update,
  [types.DELETE_SLA]: MutationHelpers.destroy,
  [types.SOFT_DELETE_SLA](_state, id) {
    const index = _state.records.findIndex(record => record.id === id);
    if (index !== -1) {
      _state.records[index] = {
        ..._state.records[index],
        disabled: true,
        disabled_at: new Date().toISOString(),
      };
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
